import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import { Theme } from '@mui/material/styles';
import makeStyles from '@mui/styles/makeStyles';
import CircularProgress from '@mui/material/CircularProgress';
import { Form, Formik } from 'formik';
import * as Yup from 'yup';
import { useMutation } from '@apollo/client';
import { StudentAssessment } from 'types';
import CREATE_STUDENT_ASSESSMENT from 'graph/queries/student-assessment/create-student-assessment';
import UPDATE_STUDENT_ASSESSMENT from 'graph/queries/student-assessment/update-student-assessment';
import { translate } from '@lwtears/lwt-common-frontend/lib/@common/util/i18n-util';
import DatePickerField from 'components/common/FormFields/DatePickerField';
import InputField from 'components/common/FormFields/InputField';

const t = (key) => translate('ui.assessments', key);

const OverviewForm = (props: {
  studentAssessment: any;
  studentId: any;
  assessmentId: any;
  next: any;
}): React.ReactElement => {
  const [createStudentAssessment] = useMutation<{
    createStudentAssessment: StudentAssessment;
  }>(CREATE_STUDENT_ASSESSMENT, {
    variables: {
      data: {
        assessmentId: 0,
        studentId: 0,
        dateAdministered: '',
        comments: '',
      },
    },
  });
  const [updateStudentAssessment] = useMutation<{
    updateStudentAssessment: StudentAssessment;
  }>(UPDATE_STUDENT_ASSESSMENT, {
    variables: {
      where: {
        id: '',
      },
      data: {
        dateAdministered: '',
        comments: '',
      },
    },
  });

  const classes = useStyles();

  const today = new Date();
  const minDate = new Date(
    new Date(new Date().setFullYear(today.getFullYear() - 2)).setMonth(0)
  ).setDate(1);

  const initialValues: { [key: string]: any } = {
    dateAdministered: today,
    comments: '',
  };

  if (props.studentAssessment) {
    // fill with existing values
    initialValues.dateAdministered = props.studentAssessment.dateAdministered;
    initialValues.comments = props.studentAssessment.comments ?? '';
  }

  const validationSchema = Yup.object().shape({
    ['dateAdministered']: Yup.date()
      .nullable()
      .max(new Date(new Date().setDate(today.getDate() + 1)), t('futureDateError')),
    ['comments']: Yup.string().nullable(),
  });

  async function _handleSubmit(values: any, actions: any) {
    if (props.studentAssessment) {
      await updateStudentAssessment({
        variables: {
          where: { id: props.studentAssessment.id },
          data: {
            dateAdministered: values['dateAdministered'],
            comments: values['comments'],
          },
        },
      });
    } else {
      await createStudentAssessment({
        variables: {
          data: {
            assessmentId: props.assessmentId,
            studentId: props.studentId,
            dateAdministered: values['dateAdministered'] ? values['dateAdministered'] : today,
            comments: values['comments'],
          },
        },
      });
    }
    actions.setSubmitting(false);
    props.next();
  }

  return (
    <Grid item xs={8}>
      <Typography variant="h2" component="h2" className={classes.sectionHeader}>
        {t('overview')}
      </Typography>
      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={_handleSubmit}
      >
        {({ isSubmitting }) => (
          <Form id="overview" style={{ flexGrow: 1 }}>
            {isSubmitting && <CircularProgress className={classes.centered} size={24} />}
            <div
              className={`${classes.formSection}${isSubmitting ? ` ${classes.loading}` : ''}`}
              style={{ flexGrow: 1 }}
            >
              <Grid container item xs={12}>
                <Grid item xs={6}>
                  <Typography variant="h4" className={classes.pb4}>
                    {t('date')}
                  </Typography>
                  <Typography variant="body1" className={classes.pb4}>
                    {t('overviewQuestion')}
                  </Typography>
                  <div className={classes.pb4}>
                    <DatePickerField
                      name="dateAdministered"
                      format="MM/dd/yy"
                      views={['year', 'month', 'day']}
                      minDate={minDate}
                      maxDate={today}
                    />
                  </div>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="h4" className={classes.pb4}>
                    {t('assessmentForms')}
                  </Typography>
                  <Typography variant="body1" className={classes.pb4}>
                    {t('assessmentFormsText')}
                  </Typography>
                </Grid>
              </Grid>
              <Typography variant="h4" className={classes.pb4}>
                {t('comments')}
              </Typography>
              <div className={classes.pb4}>
                <InputField fullWidth name="comments" />
              </div>
            </div>
            <div className={classes.footerGroup}>
              <div>
                <Button
                  variant="contained"
                  disabled={isSubmitting}
                  type="submit"
                  color="primary"
                  className={classes.btnFilled}
                >
                  {t('next')}
                </Button>
              </div>
            </div>
          </Form>
        )}
      </Formik>
    </Grid>
  );
};

export default OverviewForm;

const useStyles = makeStyles((theme: Theme) => {
  return {
    pb4: {
      paddingBottom: theme.spacing(4),
    },
    sectionHeader: {
      color: theme.palette.primary.main,
    },
    btnFilled: {
      borderRadius: '100px',
      paddingTop: theme.spacing(1),
      paddingLeft: theme.spacing(4),
      paddingBottom: theme.spacing(1),
      paddingRight: theme.spacing(4),
      lineHeight: '1rem',
    },
    formSection: {
      marginTop: theme.spacing(4),
    },
    footerGroup: {
      display: 'flex',
      justifyContent: 'flex-end',
    },
    loading: {
      opacity: 0.4,
    },
    centered: {
      position: 'absolute',
      top: '50%',
      left: '50%',
      marginRight: '-50%',
    },
    highlighted: {
      color: '#DC20AD',
    },
    ul: {
      listStyle: 'none',
      padding: 0,
    },
  };
});
